import type { ValidationError } from 'yup';
import type { MessageResponse } from '@api/ringil-api.v2.tsx';
import type { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { describeRouteIssue, RouteOrderIssue } from '@app/pages/expedition/create/routeRules.ts';
import { formatBEValidationMessage } from '@hooks/useBEValidation/formatBEValidationMessage.ts';
import {
  buildValidationRows,
  GroupRef,
  PathToken,
  SummaryRow,
  Translate,
  ValidationSummaryConfig,
} from '@components/RingilComponents/ValidationSummary/validationSummary.ts';

/**
 * How the create-shipment form's validation paths read as a list.
 *
 * Three things can stop the form being saved and each used to report itself its own way: yup errors
 * turned a field red somewhere down the page, route issues raised a toast of their own, and the
 * backend's answer went into a separate red box. They are the same question to whoever is looking at
 * the screen — what is left to fix — so they end up in one list.
 */

const LABEL_KEY_PREFIX = 'ExpeditionErrorSummary.field';

/** Group order. Follows the page: parameters at the top, then the stops, then the carrier. */
const GROUP_ORDER = {
  backend: -100,
  shipment: 0,
  route: 50,
  /** Plus the stop's index, so the stops stay in route order and sit between the two. */
  stopBase: 100,
  carrier: 1000,
};

const stopName = (location: any): string | undefined => location?.location?.locationName ?? location?.manualAddress?.name ?? undefined;

const stopGroup = (index: number, location: any, tr: Translate): GroupRef => {
  const name = stopName(location);
  return {
    name: name
      ? tr('ExpeditionErrorSummary.group.stopNamed', 'Stop {{position}}: {{name}}', { position: index + 1, name })
      : tr('ExpeditionErrorSummary.group.stop', 'Stop {{position}}', { position: index + 1 }),
    order: GROUP_ORDER.stopBase + index,
    consumed: 2,
  };
};

export const expeditionSummaryConfig: ValidationSummaryConfig = {
  labelKeyPrefix: LABEL_KEY_PREFIX,

  labels: {
    // Shipment-level parameters, shown as tags above the route.
    expeditionType: 'Type of transport',
    transportationType: 'Transportation type',
    cargoType: 'Cargo type',
    cargoTypeCode: 'Cargo type',
    adrWeight: 'ADR weight',
    truckTypeCode: 'Truck type',
    name: 'Name',
    folders: 'Folder',

    // Stop location.
    companyLocationId: 'Location',
    localityMode: 'Location',
    selectedPerson: 'Contact person',
    city: 'City',
    streetNr: 'Street and number',
    countryCode3: 'Country',
    postalCode: 'Postal code',
    timezoneId: 'Time zone',
    mainContactEmail: 'Contact e-mail',
    mainContactPhone: 'Contact phone',
    mainContactPerson: 'Contact person',
    email: 'E-mail',

    // Timeslots.
    dayWithInterval: 'Time slot',
    intervalError: 'Time slot intervals',
    day: 'Day',

    // Orders and their cargo.
    orders: 'Orders',
    orderName: 'Order name',
    externalOrders: 'Order numbers',
    items: 'Cargo',
    amount: 'Quantity',
    cargoTemplateCode: 'Handling unit',
    width: 'Width',
    height: 'Height',
    length: 'Length',
    weight: 'Weight',
    orderAmount: 'Amount',
    orderDescription: 'Description',
    orderValue: 'Value of goods',
    orderTotalWeight: 'Total weight',
    unloadingLocationId: 'Unloading location',

    // Carrier and distribution.
    price: 'Price',
    currency: 'Currency',
    selectedItems: 'Carriers',
    distributionType: 'Selection procedure',
    responseDeadlineOffsetValue: 'Offer deadline',
    responseDeadlineLocalTime: 'Offer deadline',
    ladenFactorValue: 'Laden factor',
    contractedCarriers: 'Contracted carriers',
  },

  collections: {
    orders: (index, order, tr) => order?.orderName || tr('ExpeditionErrorSummary.entry.order', 'Order {{position}}', { position: index + 1 }),
    items: (index, _item, tr) => tr('ExpeditionErrorSummary.entry.item', 'Item {{position}}', { position: index + 1 }),
    additionalPersons: (index, person, tr) => person?.name || tr('ExpeditionErrorSummary.entry.contact', 'Contact {{position}}', { position: index + 1 }),
    dayWithInterval: (index, day, tr) => day?.day || tr('ExpeditionErrorSummary.entry.day', 'Day {{position}}', { position: index + 1 }),
    intervals: (index, _interval, tr) => tr('ExpeditionErrorSummary.entry.interval', 'Interval {{position}}', { position: index + 1 }),
    selectedItems: (index, carrier, tr) => carrier?.name || tr('ExpeditionErrorSummary.entry.carrier', 'Carrier {{position}}', { position: index + 1 }),
    contractedCarriers: (index, carrier, tr) => carrier?.carrierName || tr('ExpeditionErrorSummary.entry.carrier', 'Carrier {{position}}', { position: index + 1 }),
  },

  resolveGroup: (tokens: PathToken[], values: NewExpeditionForm, tr: Translate): GroupRef | null => {
    const [first, second] = tokens;
    if (first?.kind !== 'name') return null;

    if (first.name === 'locations' && second?.kind === 'index') return stopGroup(second.index, values?.locations?.[second.index], tr);

    if (first.name === 'distribution') {
      return { name: tr('ExpeditionErrorSummary.group.carrier', 'Carrier'), order: GROUP_ORDER.carrier, consumed: 1 };
    }

    return null;
  },

  defaultGroup: (tr: Translate) => ({ name: tr('ExpeditionErrorSummary.group.shipment', 'Shipment details'), order: GROUP_ORDER.shipment }),
};

/** Route issues are about orders spanning stops, so they get their own group rather than a stop's. */
const routeIssueRows = (issues: RouteOrderIssue[], tr: Translate): SummaryRow[] =>
  (issues ?? []).map(issue => ({
    key: `route-${issue.orderId}-${issue.type}`,
    group: tr('ExpeditionErrorSummary.group.route', 'Route'),
    groupOrder: GROUP_ORDER.route,
    message: describeRouteIssue(issue, tr),
  }));

/**
 * Backend rejections, kept at the top: they are the answer to an attempt that already went out, so
 * they are worth reading before the rules the page checked on its own.
 */
const backendRows = (messages: MessageResponse[], tr: Translate): SummaryRow[] =>
  (messages ?? []).map((message, index) => ({
    key: `be-${message.path ?? index}-${index}`,
    group: tr('ExpeditionErrorSummary.group.backend', 'Reported by the server'),
    groupOrder: GROUP_ORDER.backend,
    message: formatBEValidationMessage(message, tr),
  }));

export interface ExpeditionErrorSources {
  validationErrors: ValidationError[];
  routeIssues: RouteOrderIssue[];
  backendErrors: MessageResponse[];
  values: NewExpeditionForm;
}

/** Every reason the shipment cannot be created yet, as rows ready for the summary panel. */
export const buildExpeditionErrorRows = ({ validationErrors, routeIssues, backendErrors, values }: ExpeditionErrorSources, tr: Translate): SummaryRow[] => [
  ...backendRows(backendErrors, tr),
  ...routeIssueRows(routeIssues, tr),
  ...buildValidationRows(validationErrors, values, tr, expeditionSummaryConfig),
];
